<template>
    <section class="w-full md:max-w-[50rem] mx-auto px-5 md:px-0">
        <ConfirmationModal 
            v-if="askConfirmation" 
            :title="confirmationTitle" 
            :text="confirmationText" 
            :subText="confirmationSubText"
            @emitResponse="checkConfirmationResponse" 
        />

        <div class="text-secondary w-full mt-10">
            <div class="rounded shadow p-3 text-center bg-white mb-10 pb-10">
                <p class="text-2xl font-bold py-4">
                    {{ translate('my profile', 'ucfirst') }}
                </p>

                <div class="flex w-full my-2">
                    <div class="w-full text-left">
                        <p class="mb-2">{{ translate('name', 'ucfirst') }}</p>
                        <InputText 
                            v-model="userForm.name"
                            class="w-full py-2 px-3 rounded-lg border border-color-iron border-focus-purple-suit"
                            type="text"
                            placeholder="John Doe"
                        />
                    </div>
                </div>

                <div class="flex w-full gap-4 mb-6 mt-2">
                    <div class="w-full md:w-2/4 text-left">
                        <p class="mb-2">{{ translate('birth date', 'ucfirst') }}</p>
                        <DatePicker 
                            v-model="userForm.birthDate" 
                            showIcon 
                            fluid 
                            iconDisplay="input" 
                            :dateFormat="getFormatByLanguage()"
                        />
                    </div>
                    <div class="w-full md:w-2/4 text-left">
                        <p class="mb-2">{{ translate('language', 'ucfirst') }}</p>
                        <Select 
                            v-model="userForm.language" 
                            :options="selectLanguages"
                            filter 
                            :showClear="true"
                            optionLabel="value" 
                            :placeholder="translate('select a language', 'ucfirst')" 
                            class="w-full" 
                        />
                    </div>
                </div>

                <div class="flex w-full gap-4 mb-6 mt-2">
                    <div class="w-full md:w-2/4 text-left">
                        <p class="mb-2">{{ translate('country', 'ucfirst') }}</p>
                        <Select 
                            v-model="userForm.country" 
                            :options="selectCountries"
                            filter 
                            :showClear="true"
                            optionLabel="value" 
                            :placeholder="translate('select a country', 'ucfirst')" 
                            class="w-full"
                            @change="userForm.city = null" 
                        />
                    </div>
                    <div class="w-full md:w-2/4 text-left">
                        <p class="mb-2">{{ translate('city', 'ucfirst') }}</p>
                        <InputText 
                            v-model="userForm.city"
                            class="w-full py-2 px-3 rounded-lg border border-color-iron border-focus-purple-suit"
                            type="text"
                            :placeholder="!userForm.country ? '' : translate('enter your city', 'ucfirst')"
                            :disabled="!userForm.country"
                        />
                    </div>
                </div>
                <div class="text-right mt-12">
                    <Button 
                        class="w-full md:w-1/3 bg-purple-suit-forced p-3"
                        :label="translate('save changes', 'capitalize')"
                        icon="pi pi-pen-to-square"
                        @click.prevent="attemptSavingProfile"
                        :loading="loading"
                    />
                </div>

                <div class="w-full flex items-center justify-between gap-3 mt-10">
                    <small class="font-bold text-lg">{{ translate('advanced settings', 'ucfirst') }}</small>
                    <Divider class="flex-grow" />
                </div>

                <transition name="p-connected-overlay">
                    <section class="mt-5 text-left rounded-lg overflow-hidden">
                        <Tabs :value="selectedAdvancedTab">
                            <TabList>
                                <Tab value="email" @click="selectedAdvancedTab = 'email'">
                                    <i class="pi pi-envelope mr-2"></i> {{ translate('email', 'capitalize') }}
                                </Tab>
                                <Tab value="password" @click="selectedAdvancedTab = 'password'">
                                    <i class="pi pi-key mr-2"></i> {{ translate('password', 'capitalize') }}
                                </Tab>
                            </TabList>
                        </Tabs>

                        <div class="my-3 bg-gray-50">
                            <div v-if="selectedAdvancedTab === 'email'" class="text-right">
                                <InputText 
                                    class="w-full" 
                                    v-model="credentialsForm.email" 
                                    :placeholder="translate('new email', 'ucfirst')" 
                                />
                                <Button 
                                    :label="translate('update email', 'capitalize')" 
                                    icon="pi pi-check"
                                    @click="attemptChangingEmail"
                                    :loading="changingEmail"
                                    class="w-auto mt-4 bg-purple-suit-forced whitespace-nowrap"
                                />
                            </div>

                            <div v-if="selectedAdvancedTab === 'password'" class="flex flex-col gap-4">
                                <div class="flex flex-col md:flex-row gap-4">
                                    <Password 
                                        v-model="credentialsForm.oldPassword" 
                                        :placeholder="translate('current password', 'ucfirst')" 
                                        class="w-full"
                                        toggleMask fluid :feedback="false"
                                    />
                                    <Password 
                                        v-model="credentialsForm.newPassword" 
                                        :placeholder="translate('new password', 'ucfirst')"
                                        class="w-full" 
                                        toggleMask fluid
                                    />
                                </div>
                                <Button 
                                    :label="translate('change password', 'capitalize')" 
                                    icon="pi pi-lock"
                                    @click="attemptChangingPassword"
                                    :loading="changingPassword"
                                    class="bg-purple-suit-forced md:w-1/3 self-end"
                                />
                            </div>
                        </div>
                    </section>
                </transition>
            </div>
        </div>
    </section>
</template>

<script setup>
import { onMounted, reactive, ref, watch } from 'vue';
import { useToast, InputText, DatePicker, Button, Select, Divider, Tabs, TabList, Tab, Password } from 'primevue';

// Stores & Composables
import { useContextStore } from '@/stores/context';
import { useAuthStore } from '@/stores/auth';
import { translate } from '@/composables/translate';
import { ucfirst } from '@/composables/textUtils';
import { showToast } from '@/composables/toast';
import utils from '@/composables/utils';
import urlHandler from '@/composables/urlHandler';
import cookiesHandler from '@/composables/cookiesHandler';

// Handlers & Services
import UserService from '@/services/UserService';
import CountryService from '@/services/CountryService';
import AuthUserService from '@/services/AuthUserService';
import ValidatorHandler from '@/composables/handlers/ValidatorHandler';
import DateHandler from '@/composables/handlers/DateHandler';

// Components
import ConfirmationModal from '@/components/Utils/ConfirmationModal.vue';

const toast = useToast();
const authStore = useAuthStore();
const contextStore = useContextStore();

// --- States ---
const loading = ref(true);
const selectedAdvancedTab = ref('email');
const changingEmail = ref(false);
const changingPassword = ref(false);

const userForm = reactive({
    uuid: null,
    name: null,
    email: null,
    birthDate: null,
    city: null,
    country: null,
    language: null
});

const credentialsForm = reactive({
    email: '',
    oldPassword: '',
    newPassword: ''
});

const languagesList = contextStore.getLanguages;
const selectLanguages = ref([]);
const langsById = ref({});

const selectCountries = ref([]);
const countriesById = ref({});

// --- Lifecycle & Watchers ---
onMounted(() => {
    getComboDatas();
});

watch(
    () => contextStore.getLang,
    () => getComboDatas()
);

// --- Data Loading ---
async function getComboDatas() {
    await Promise.all([getLanguagesList(), getCountriesList()]);
    loadAuthenticatedUserOnForm();
    loading.value = false;
}

async function getLanguagesList() {
    let selectListValues = [];
    for (let language of languagesList.parsedItems()) {
        const langData = { code: language.code, value: ucfirst(language.getName()) };
        langsById.value[language.id] = langData;
        selectListValues.push(langData);
    }
    selectLanguages.value = selectListValues;
}

async function getCountriesList() {
    const countries = await CountryService.getCountries();
    let selectListValues = [];
    for (let country of countries) {
        const countryData = { code: country.code, value: ucfirst(country.getName()) };
        countriesById.value[country.id] = countryData;
        selectListValues.push(countryData);
    }
    selectCountries.value = selectListValues;
}

function loadAuthenticatedUserOnForm(sentUser = null) {
    const user = sentUser || authStore.getUser;
    for (let attr of Object.keys(userForm)) {
        let value = user[attr];
        let data = null;

        switch (attr) {
            case 'language':
                data = langsById.value[value] || null;
                value = { code: data?.code || null, value: data?.value ? ucfirst(data.value) : null };
                break;
            case 'country':
                data = countriesById.value[value] || null;
                value = { code: data?.code || null, value: data?.value ? ucfirst(data.value) : null };
                break;
            case 'birthDate':
                value = user.getLocalizedBirthDate();
                break;
        }
        userForm[attr] = value;
    }
}

// --- Logic: Profile Save ---
async function save() {
    let savingData = utils.copyVariable(userForm);
    if (savingData.language) savingData.language = savingData.language.code;
    if (savingData.country) savingData.country = savingData.country.code;

    loading.value = true;
    const result = await UserService.saveUser(savingData, savingData.uuid);
    loading.value = false;

    if (!result.success()) {
        showToast(toast, result.getMessage(), 'error');
        return;
    }
    showToast(toast, translate('profile updated', 'ucfirst'));
}

// --- Logic: Credentials ---
async function executeEmailChange() {
    if (credentialsForm.email == authStore.getUser.email) {
        showToast(toast, translate('the email must be different', 'ucfirst'), 'error');
        return;
    }

    changingEmail.value = true;
    const response = await UserService.changeEmail(credentialsForm.email);
    
    if (response.success()) {
        showToast(toast, translate('email changed', 'ucfirst'));
        AuthUserService.logout();
        cookiesHandler.setEncryptedCookie('TM_VE_SENT', DateHandler.nowDateFormated(), 3);
        urlHandler.redirectAndScroll('/email/verify/');

    } else {
        showToast(toast, response.getMessage(), 'error');
    }
    changingEmail.value = false;
}

async function executePasswordChange() {
    changingPassword.value = true;
    const response = await UserService.changePassword(credentialsForm.oldPassword, credentialsForm.newPassword);
    if (response.success()) {
        showToast(toast, translate('password updated', 'ucfirst'));
        credentialsForm.oldPassword = '';
        credentialsForm.newPassword = '';
    } else {
        showToast(toast, response.getMessage(), 'error');
    }
    changingPassword.value = false;
}

// --- Confirmation Modal Management ---
const askConfirmation = ref(false);
const confirmationTitle = ref('');
const confirmationText = ref('');
const confirmationSubText = ref('');
const callbackOnConfirmation = ref(null);

function checkConfirmationResponse(value) {
    if (!value || ![true, undefined].includes(value.confirmation)) {
        askConfirmation.value = false;
        return;
    }
    
    askConfirmation.value = false;
    if (value.confirmation && callbackOnConfirmation.value) {
        callbackOnConfirmation.value();
    }
}

function attemptSavingProfile() {
    confirmationTitle.value = translate('save changes', 'capitalize');
    confirmationText.value = translate('are you sure you want to update your profile?', 'ucfirst');
    confirmationSubText.value = '';
    callbackOnConfirmation.value = save;
    askConfirmation.value = true;
}

function attemptChangingEmail() {
    if (!ValidatorHandler.checkEmail(credentialsForm.email)) {
        showToast(toast, translate('email is invalid', 'ucfirst'), 'info');
        return;
    }
    confirmationTitle.value = translate('change email', 'capitalize');
    confirmationText.value = translate('are you sure you want to change your email?', 'ucfirst');
    confirmationSubText.value = translate('this action can not be undone and you will be logged off!', 'ucfirst');
    callbackOnConfirmation.value = executeEmailChange;
    askConfirmation.value = true;
}

function attemptChangingPassword() {
    if (!credentialsForm.oldPassword || !credentialsForm.newPassword) {
        showToast(toast, translate('fill in all password fields', 'ucfirst'), 'info');
        return;
    }
    if (credentialsForm.newPassword === credentialsForm.oldPassword) {
        showToast(toast, translate('the passwords must be different', 'ucfirst'), 'info');
        return;
    }
    const validation = ValidatorHandler.checkPassword(credentialsForm.newPassword);
    if (!validation.success) {
        showToast(toast, validation.message, 'info');
        return;
    }

    confirmationTitle.value = translate('change password', 'capitalize');
    confirmationText.value = translate('are you sure you want to change your password?', 'ucfirst');
    confirmationSubText.value = translate('this action can not be undone!', 'ucfirst');
    callbackOnConfirmation.value = executePasswordChange;
    askConfirmation.value = true;
}

function getFormatByLanguage() {
    const lang = contextStore.getLang;
    return (lang === 'pt' || lang === 'es') ? 'dd/mm/yy' : 'yy/mm/dd';
}
</script>